import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import Button from "@/components/Button";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import IconButton from "@/components/IconButton";
import ObjectHeading from "@/components/ObjectHeading";
import usePageTitle from "@/hooks/usePageTitle";
import { useNavigate, type Path } from "@/router";
import { useEventsBasePath } from "@/pages/events/_eventsBasePathContext";
import { isSameDay, toFormat } from "@/lib/dateHelpers";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import useCapabilities, { useHasRoles, useDenyAuthorization } from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import type { EventWrapper } from "../types";
import FinalizeAttendanceTable from "../(tabbed)/attendance/_components/FinalizeAttendanceTable";
import { FINALIZE_ATTENDANCE_MUTATION } from "./mutations";
import { useEventDetailQueryDocument } from "../useEventDetailQuery";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

export default function FinalizeAttendancePage() {
  const { eventSlug = "" } = useParams();
  const basePath = useEventsBasePath();
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();

  usePageTitle("/events/:eventSlug/attendance/finalize", "Events | Finalize Attendance");

  const { loaded: capabilitiesLoaded } = useCapabilities();
  const isAuthorized = useHasRoles([ROLE.EDUCATION_STAFF, ROLE.GLOBAL_ADMIN]);
  const denyAuthorization = useDenyAuthorization();

  useEffect(() => {
    if (capabilitiesLoaded && !isAuthorized) {
      denyAuthorization();
    }
  }, [capabilitiesLoaded, isAuthorized, denyAuthorization]);

  const eventDetailQuery = useEventDetailQueryDocument();

  const { data } = useQuery<EventWrapper>(eventDetailQuery, {
    variables: { slug: eventSlug },
    skip: !isAuthorized
  });
  const event = data?.event;
  const [finalizeAttendance, { loading: finalizingAttendance }] = useMutation(FINALIZE_ATTENDANCE_MUTATION);

  if (!capabilitiesLoaded || !isAuthorized) {
    return null;
  }

  const handleBack = () => {
    navigate(`${basePath}/${eventSlug}/attendance` as Path);
  };

  const handleFinalizeAttendance = async () => {
    if (!event?.id) {
      showErrorSnackbar("Unable to finalize attendance. Try refreshing the page.");
      return;
    }

    try {
      const { data: mutationData } = await finalizeAttendance({
        variables: { eventId: event.id },
        refetchQueries: [{ query: eventDetailQuery, variables: { slug: eventSlug } }],
        awaitRefetchQueries: true
      });
      const payload = mutationData?.finalizeAttendance;

      if (!payload?.success) {
        showErrorSnackbar(payload?.message ?? "Unable to finalize attendance.");
        return;
      }

      showSnackbar(payload.message || "Attendance finalized successfully.", { close: true });
      trackEvent(
        EVENT_ANALYTICS_EVENTS.attendanceFinalized,
        getEventAnalyticsData({
          source: "attendance",
          eventId: event.id,
          eventSlug,
          action: "finalize"
        })
      );
      handleBack();
    } catch {
      showErrorSnackbar("Unable to finalize attendance. Try again.");
    }
  };

  const eventDateLabel = event
    ? isSameDay(event.startsAt, event.endsAt, event.timezone)
      ? toFormat(event.startsAt) || ""
      : `${toFormat(event.startsAt)} - ${toFormat(event.endsAt)}`
    : "";

  const cancelButton = (
    <div className="flex items-center cursor-pointer" onClick={handleBack}>
      <IconButton name="arrow_back" data-testid="back" iconClassName="text-sys-brand-on-surface" />
      <span className="text-title-large">Cancel</span>
    </div>
  );

  return (
    <FocusedPageLayout
      cancelButton={cancelButton}
      stickyActions
      actions={
        <Button
          variant="filled"
          onClick={handleFinalizeAttendance}
          disabled={finalizingAttendance || !event?.id}
          testId="finalize-attendance-button"
        >
          Finalize Attendance
        </Button>
      }
    >
      <div data-testid="finalize-attendance-page">
        {event && (
          <ObjectHeading
            title={event.title}
            metaData={[
              ...(event.eventCode ? [{ text: event.eventCode, label: "Event ID" }] : []),
              ...(eventDateLabel ? [{ text: eventDateLabel, label: "Date" }] : [])
            ]}
            iconName="today"
            isDetailsView
            showFullMetadataInCondensed
          />
        )}
        <FinalizeAttendanceTable eventSlug={eventSlug} />
      </div>
    </FocusedPageLayout>
  );
}
